// TES-1961 (PT-TSK-Consumer-UI, Option 1) — Task-Type-Config: die EINE config-getriebene Quelle für die
// Darstellung des gespiegelten PT-TSK-Task-Status im Consumer-UI (Status/Fortschritt der verknüpften
// Task-Gruppe am Mangel, §6b). Neue Status/Labels werden hier gepflegt — kein Frontend-Release pro Status.
//
// VERTRAGS-INVARIANTE: die Status-Keys MÜSSEN deckungsgleich mit dem PT-TSK-Vertrag bleiben
// (`contracts/pt-tsk.yaml` x-task-status-enum). Ein CDC-Test (taskTypeConfig.contract.test.ts) erzwingt das,
// damit ein Vertrags-Drift (neuer/entfallener Status beim echten PT-TSK ~2026-07-15) sofort rot wird.
//
// LOCK-INVARIANTE (Single-Source mit Migr. 304 `_defect_open_task_count` + useDefectTaskLinks.openTaskLinkCount):
// nur `done`/`completed` sind erledigt/nicht-sperrend. `cancelled` zählt NICHT als erledigt (bleibt „offen").

// Kanonische Vertrags-Statusliste, gegen die der CDC-Test (taskTypeConfig.contract.test.ts)
// contracts/pt-tsk.yaml x-task-status-enum prüft. Außer der Typ-Ableitung gibt es bewusst keinen
// Prod-Consumer des Werts — der Export existiert als test-referenzierte Vertrags-Oberfläche.
// fallow-ignore-next-line unused-export
export const PT_TASK_STATUSES = ["open", "in_progress", "done", "completed", "cancelled"] as const;
// Modul-intern (nur als Record-Key/Cast verwendet) — kein externer Consumer, daher kein Export.
type PtTaskStatus = (typeof PT_TASK_STATUSES)[number];

export interface TaskStatusConfig {
  /** i18n-Key (wiederverwendet defects.taskLinks.status.*). */
  labelKey: string;
  /** EntityChip-Tonalität. */
  tone: "default" | "muted" | "primary";
  /** Zählt als erledigt (done/completed) → in den Fortschritt. */
  terminal: boolean;
}

/** Config-getriebene Status-Map (Task-Type-Config) — modul-intern; Zugriff über resolveStatusConfig. */
const taskStatusConfig: Record<PtTaskStatus, TaskStatusConfig> = {
  open: { labelKey: "defects.taskLinks.status.open", tone: "default", terminal: false },
  in_progress: {
    labelKey: "defects.taskLinks.status.in_progress",
    tone: "primary",
    terminal: false,
  },
  done: { labelKey: "defects.taskLinks.status.done", tone: "muted", terminal: true },
  completed: { labelKey: "defects.taskLinks.status.completed", tone: "muted", terminal: true },
  cancelled: { labelKey: "defects.taskLinks.status.cancelled", tone: "muted", terminal: false },
};

/**
 * Löst die Render-Config für einen (evtl. null/unbekannten) gespiegelten Status auf.
 * Defensiv: ein unbekannter Status (Vertrags-Drift, der den CDC-Test noch nicht gerissen hat) wird
 * neutral wie „open" dargestellt statt zu crashen.
 */
export function resolveStatusConfig(status: string | null | undefined): TaskStatusConfig {
  if (status && status in taskStatusConfig) {
    return taskStatusConfig[status as PtTaskStatus];
  }
  return taskStatusConfig.open;
}

/** Fortschritt einer verknüpften Task-Gruppe (config-getrieben, deckt sich mit der Lock-Invariante). */
export interface TaskGroupProgressInfo {
  total: number;
  /** erledigt = done/completed. */
  done: number;
  /** noch offen (alles außer done/completed — inkl. cancelled, analog openTaskLinkCount). */
  open: number;
  /** 0–100, gerundet; 0 wenn keine Tasks. */
  percent: number;
  /** alle Tasks erledigt (total > 0 und open === 0). */
  allDone: boolean;
}

function isTerminalDone(status: string | null | undefined): boolean {
  // Config ist die einzige Wahrheit: resolveStatusConfig deckt null/unbekannt (→ open, terminal:false) ab.
  return resolveStatusConfig(status).terminal;
}

/** Berechnet den Gruppen-Fortschritt aus den gespiegelten Status der Task-Links. */
export function computeTaskGroupProgress(
  statuses: (string | null | undefined)[],
): TaskGroupProgressInfo {
  const total = statuses.length;
  const done = statuses.filter(isTerminalDone).length;
  const open = total - done;
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return { total, done, open, percent, allDone: total > 0 && open === 0 };
}
